import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { UserPlus, UserMinus, AlertTriangle, ArrowRight, Calendar } from "lucide-react";

function currentMonthYm() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function buildMonthOptions(count = 24) {
  const out = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
    out.push({ ym, label });
  }
  return out;
}

function humanMonth(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
}

export default function HRTilesWidget() {
  const [month, setMonth] = useState(currentMonthYm());
  const [tiles, setTiles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customOpen, setCustomOpen] = useState(false);
  const [customMonth, setCustomMonth] = useState("");

  const monthOptions = useMemo(() => buildMonthOptions(24), []);

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/dashboard/hr-tiles?month=${month}`);
        if (live) setTiles(data);
      } catch {
        if (live) setTiles(null);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [month]);

  if (!tiles && !loading) return null;
  const label = humanMonth(month);

  const items = [
    {
      key: "joiners",
      label: `New Joiners · ${label}`,
      count: tiles?.new_joiners?.count ?? 0,
      icon: UserPlus,
      accent: "text-emerald-600",
      bgHex: "from-emerald-50 to-teal-50",
      link: `/dashboard/joiners?month=${month}`,
    },
    {
      key: "left",
      label: `Left · ${label}`,
      count: tiles?.left?.count ?? 0,
      icon: UserMinus,
      accent: "text-red-600",
      bgHex: "from-red-50 to-orange-50",
      link: `/dashboard/left?month=${month}`,
    },
    {
      key: "ghost",
      label: `Zero Attendance · ${label}`,
      count: tiles?.zero_attendance?.count ?? 0,
      icon: AlertTriangle,
      accent: "text-amber-600",
      bgHex: "from-amber-50 to-yellow-50",
      link: `/dashboard/zero-attendance?month=${month}`,
    },
  ];

  return (
    <div className="space-y-3" data-testid="hr-tiles-widget">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
          <Calendar className="h-3.5 w-3.5" />
          HR Snapshot
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="hr-tiles-month" className="text-[11px] uppercase tracking-wider text-slate-500">
            Month
          </label>
          <select
            id="hr-tiles-month"
            data-testid="hr-tiles-month-select"
            value={monthOptions.some(o => o.ym === month) ? month : "__custom__"}
            onChange={(e) => {
              if (e.target.value === "__custom__") {
                setCustomOpen(true);
              } else {
                setMonth(e.target.value);
              }
            }}
            className="text-xs border border-slate-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-red-400"
          >
            {monthOptions.map((o) => (
              <option key={o.ym} value={o.ym}>{o.label}</option>
            ))}
            <option value="__custom__">Custom…</option>
          </select>
          {customOpen && (
            <div className="flex items-center gap-1">
              <input
                type="month"
                data-testid="hr-tiles-month-custom"
                value={customMonth}
                onChange={(e) => setCustomMonth(e.target.value)}
                className="text-xs border border-slate-200 rounded px-2 py-1 bg-white"
              />
              <button
                type="button"
                onClick={() => {
                  if (customMonth) setMonth(customMonth);
                  setCustomOpen(false);
                }}
                className="text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700"
              >
                Apply
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
        {items.map((t) => {
          const Icon = t.icon;
          const inner = (
            <Card
              className={`border-slate-200 shadow-sm hover:shadow-md transition bg-gradient-to-br ${t.bgHex}`}
              data-testid={`hr-tile-${t.key}`}
            >
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-start justify-between">
                  <div className="text-[11px] uppercase tracking-wider text-slate-600 font-medium leading-tight">
                    {t.label}
                  </div>
                  <Icon className={`h-5 w-5 ${t.accent}`} />
                </div>
                <div
                  className={`mt-2 text-2xl sm:text-3xl font-bold ${t.accent}`}
                  style={{ fontFamily: "Chivo" }}
                >
                  {loading ? "…" : t.count}
                </div>
                <div className="text-[11px] text-slate-500 mt-2 flex items-center gap-1">
                  View details <ArrowRight className="h-3 w-3" />
                </div>
                {t.key === "ghost" && t.count > 0 && (
                  <div className="text-[10px] text-amber-700 mt-2 leading-snug">
                    Zero attendance or missing from last 2 payroll runs.
                    Investigate for ghost / abandoned employees.
                  </div>
                )}
              </CardContent>
            </Card>
          );
          return <Link key={t.key} to={t.link} data-testid={`hr-tile-link-${t.key}`}>{inner}</Link>;
        })}
      </div>
    </div>
  );
}
